let BASE_URL = ''

export function configureApi(baseUrl: string) {
  BASE_URL = baseUrl
}

export function getBaseUrl(): string {
  return BASE_URL
}

let refreshPromise: Promise<boolean> | null = null
let sessionExpiredCallback: (() => void) | null = null

export function onSessionExpired(cb: () => void) {
  sessionExpiredCallback = cb
}

async function attemptRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    return res.ok
  } catch {
    return false
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    ...init,
  })

  // Paths where a 401 is expected (auth check, login, refresh) — don't intercept
  const skipIntercept = path === '/auth/me'
    || path.startsWith('/auth/refresh')
    || path.startsWith('/auth/login')
    || path.startsWith('/auth/register')

  if (res.status === 401 && !skipIntercept) {
    if (!refreshPromise) {
      refreshPromise = attemptRefresh().finally(() => { refreshPromise = null })
    }

    const refreshed = await refreshPromise
    if (refreshed) {
      const retry = await fetch(`${BASE_URL}${path}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...init?.headers,
        },
        ...init,
      })

      if (retry.ok) {
        if (retry.status === 204) return undefined as T
        return retry.json() as Promise<T>
      }
    }

    if (sessionExpiredCallback) {
      sessionExpiredCallback()
    } else if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
      window.location.href = '/'
    }
    throw new Error('Session expired')
  }

  if (!res.ok) {
    let message = `API error ${res.status}: ${path}`
    try {
      const body = await res.json()
      if (body?.error) message = body.error
    } catch { /* no json body */ }
    throw new Error(message)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get:    <T>(path: string)                  => request<T>(path),
  post:   <T>(path: string, body?: unknown)  => request<T>(path, { method: 'POST',   body: body !== undefined ? JSON.stringify(body) : undefined }),
  put:    <T>(path: string, body: unknown)   => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch:  <T>(path: string, body: unknown)   => request<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  delete: <T>(path: string)                  => request<T>(path, { method: 'DELETE' }),
}
